/**
 * 多平台影片 URL 解析和嵌入工具
 * 支援 YouTube, Vimeo, Bilibili 等主流影片平台
 */

// 支援的影片平台配置
const VIDEO_PLATFORMS = {
  youtube: {
    name: 'YouTube',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?(?:[^#]*&)?v=([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ],
    embedUrl: (videoId) => `https://www.youtube.com/embed/${videoId}`,
    aspectRatio: '16:9',
    allowFullScreen: true,
    sandboxOptions: 'allow-same-origin allow-scripts allow-forms allow-popups allow-presentation'
  },
  
  vimeo: {
    name: 'Vimeo',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
      /(?:https?:\/\/)?player\.vimeo\.com\/video\/(\d+)/
    ],
    embedUrl: (videoId) => `https://player.vimeo.com/video/${videoId}`,
    aspectRatio: '16:9',
    allowFullScreen: true,
    sandboxOptions: 'allow-same-origin allow-scripts allow-forms allow-popups allow-presentation'
  },
  
  bilibili: {
    name: 'Bilibili',
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?bilibili\.com\/video\/(BV[a-zA-Z0-9]+)/,
      /(?:https?:\/\/)?(?:www\.)?bilibili\.com\/video\/(av\d+)/,
      /(?:https?:\/\/)?b23\.tv\/([a-zA-Z0-9]+)/ // 短網址
    ],
    embedUrl: (videoId) => `https://player.bilibili.com/player.html?bvid=${videoId}&autoplay=0`,
    aspectRatio: '16:9',
    allowFullScreen: true,
    sandboxOptions: 'allow-same-origin allow-scripts allow-forms allow-popups allow-presentation'
  },

  soundon: {
    name: 'SoundOn',
    type: 'audio',
    patterns: [
      /(?:https?:\/\/)?player\.soundon\.fm\/p\/([a-f0-9-]+\/episodes\/[a-f0-9-]+)/i,
      /(?:https?:\/\/)?soundon\.fm\/p\/([a-f0-9-]+\/episodes\/[a-f0-9-]+)/i
    ],
    embedUrl: (idPath) => {
      const [podcastId, episodeId] = idPath.split('/episodes/');
      return `https://player.soundon.fm/embed/?podcast=${podcastId}&episode=${episodeId}`;
    },
    aspectRatio: 'audio',
    allowFullScreen: false,
    sandboxOptions: 'allow-same-origin allow-scripts allow-forms allow-popups'
  }
};

/**
 * 檢測 URL 是否為支援的影片平台
 * @param {string} url - 要檢測的 URL
 * @returns {Object|null} 包含平台資訊和影片 ID 的對象，或 null
 */
export const detectVideoPlatform = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  // 清理 URL，移除多餘的空白
  const cleanUrl = url.trim();
  
  for (const [platformKey, platform] of Object.entries(VIDEO_PLATFORMS)) {
    for (const pattern of platform.patterns) {
      const match = cleanUrl.match(pattern);
      if (match && match[1]) {
        return {
          platform: platformKey,
          platformName: platform.name,
          type: platform.type || 'video',
          videoId: match[1],
          originalUrl: cleanUrl,
          embedUrl: platform.embedUrl(match[1]),
          aspectRatio: platform.aspectRatio,
          allowFullScreen: platform.allowFullScreen,
          sandboxOptions: platform.sandboxOptions
        };
      }
    }
  }
  
  return null;
};

/**
 * 檢查 URL 是否為影片連結
 * @param {string} url - 要檢測的 URL
 * @returns {boolean} 是否為影片連結
 */
export const isVideoUrl = (url) => {
  return detectVideoPlatform(url) !== null;
};

/**
 * 獲取影片平台的嵌入 URL
 * @param {string} url - 原始影片 URL
 * @returns {string|null} 嵌入 URL 或 null
 */
export const getVideoEmbedUrl = (url) => {
  const detection = detectVideoPlatform(url);
  return detection ? detection.embedUrl : null;
};

/**
 * 獲取支援的平台列表
 * @returns {Array} 平台資訊數組
 */
export const getSupportedPlatforms = () => {
  return Object.entries(VIDEO_PLATFORMS).map(([key, platform]) => ({
    key,
    name: platform.name,
    aspectRatio: platform.aspectRatio
  }));
};

/**
 * 生成影片嵌入 HTML 屬性
 * @param {Object} videoInfo - 從 detectVideoPlatform 返回的影片資訊
 * @returns {Object} 包含 iframe 所需屬性的對象
 */
export const getVideoEmbedProps = (videoInfo) => {
  if (!videoInfo) return null;
  
  return {
    src: videoInfo.embedUrl,
    title: videoInfo.type === 'audio' ? `${videoInfo.platformName} Player` : `${videoInfo.platformName} Video Player`,
    frameBorder: "0",
    allowFullScreen: videoInfo.allowFullScreen,
    sandbox: videoInfo.sandboxOptions,
    referrerPolicy: "strict-origin-when-cross-origin",
    loading: "lazy"
  };
};

/**
 * 驗證影片 URL 是否有效
 * @param {string} url - 要驗證的 URL
 * @returns {Object} 驗證結果 { valid: boolean, platform?: string, error?: string }
 */
export const validateVideoUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return { valid: false, error: 'URL 不能為空' };
  }
  
  if (url.trim().length === 0) {
    return { valid: false, error: 'URL 不能為空白' };
  }
  
  const detection = detectVideoPlatform(url);
  if (!detection) {
    return { 
      valid: false, 
      error: '不支援的影片平台，目前支援：YouTube、Vimeo、Bilibili、SoundOn'
    };
  }
  
  // 檢查影片 ID 格式
  if (!detection.videoId || detection.videoId.length === 0) {
    return { 
      valid: false, 
      error: '無法提取有效的影片 ID' 
    };
  }
  
  return { 
    valid: true, 
    platform: detection.platform,
    platformName: detection.platformName,
    videoId: detection.videoId
  };
};

/**
 * 獲取影片縮圖 URL
 * @param {string} platform - 平台名稱
 * @param {string} videoId - 影片 ID
 * @returns {string|null} 縮圖 URL
 */
export const getVideoThumbnailUrl = (platform, videoId) => {
  switch (platform) {
    case 'youtube':
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    case 'vimeo':
      // Vimeo 需要 API 調用獲取縮圖，暫時返回 null
      return null;
    case 'bilibili':
      // Bilibili 縮圖需要額外處理，暫時返回 null
      return null;
    default:
      return null;
  }
};

/**
 * 格式化影片平台顯示名稱
 * @param {string} platform - 平台 key
 * @returns {string} 格式化後的平台名稱
 */
export const formatPlatformName = (platform) => {
  return VIDEO_PLATFORMS[platform]?.name || platform;
};